/**
 * SEO/AEO/GEO helper components.
 *
 * Adds per-route <title>, meta description, OG/Twitter, canonical, and
 * JSON-LD `BreadcrumbList` schema without touching design or UX.
 *
 * Usage on a page:
 *   <PageSeo
 *     title="Selected Work · Relatable Studio"
 *     description="..."
 *     path="/work"
 *     breadcrumbs={[{ name: "Home", path: "/" }, { name: "Work", path: "/work" }]}
 *   />
 */
import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://relatable.studio";
const DEFAULT_OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_brand-cinema-8/artifacts/3j3f8ta6_RelatableLogo-White.png";

export function PageSeo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  breadcrumbs,
  jsonLd,
}) {
  const url = `${SITE}${path}`;

  const breadcrumbLd = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: `${SITE}${b.path}`,
    })),
  } : null;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Relatable Studio" />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {breadcrumbLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      )}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export default PageSeo;
